import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Lock, Mail, User } from 'lucide-react';

export const AdminLogin = () => {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isRegister) {
        await register(formData.email, formData.password, formData.name);
      } else {
        await login(formData.email, formData.password);
      }
    } catch (err) {
      setError(t('admin.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#0F1A2E] flex items-center justify-center px-6">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            {/* Logo Icon */}
            <div className="text-[#C9A84C]">
              <svg width="50" height="50" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 5L90 25V75L50 95L10 75V25L50 5Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M50 15L80 30V70L50 85L20 70V30L50 15Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M35 45L50 35L65 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M35 55L50 45L65 55" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M35 65L50 55L65 65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            {/* Logo Text */}
            <div className="flex flex-col">
              <span className="text-[#C9A84C] font-heading text-2xl md:text-3xl font-bold tracking-wide">BEU</span>
              <span className="text-[#C9A84C]/80 text-[10px] md:text-xs tracking-widest uppercase">Finansal Danışmanlık</span>
            </div>
          </div>
          <p className="text-white/50">{t('admin.dashboard')}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="admin-login-form"
          className="glass p-8 space-y-6"
        >
          <h2 className="font-heading text-2xl font-bold text-white text-center">
            {isRegister ? 'Kayıt Ol' : t('admin.login')}
          </h2>

          {isRegister && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">
                İsim
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={20} />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required={isRegister}
                  value={formData.name}
                  onChange={handleChange}
                  data-testid="admin-name-input"
                  className="bg-[#1B2B4B]/50 border-[#C9A84C]/20 focus:border-[#C9A84C] text-white placeholder:text-white/30 rounded-none h-12 pl-10"
                  placeholder="Adınız"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/70">
              {t('admin.email')}
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={20} />
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                data-testid="admin-email-input"
                className="bg-[#1B2B4B]/50 border-[#C9A84C]/20 focus:border-[#C9A84C] text-white placeholder:text-white/30 rounded-none h-12 pl-10"
                placeholder="admin@example.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/70">
              {t('admin.password')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={20} />
              <Input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                data-testid="admin-password-input"
                className="bg-[#1B2B4B]/50 border-[#C9A84C]/20 focus:border-[#C9A84C] text-white placeholder:text-white/30 rounded-none h-12 pl-10"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-red-500 text-sm text-center">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-[#C9A84C] text-[#0F1A2E] hover:bg-[#E5D49B] rounded-none h-12 font-semibold tracking-wide disabled:opacity-50"
          >
            {loading ? 'Yükleniyor...' : isRegister ? 'Kayıt Ol' : t('admin.login')}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              data-testid="toggle-register-btn"
              className="text-[#C9A84C] text-sm hover:underline"
            >
              {isRegister ? 'Zaten hesabınız var mı? Giriş yapın' : 'Hesabınız yok mu? Kayıt olun'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
